import { ProviderModel } from './../../models/provider.model';
import { Injectable } from '@angular/core';
//
import { BaseRepository } from './base.repository';
import { BaseEntityModel } from './base-entity.model';
import { Tools } from '../utils/tools/index';
import { SearchResponseModel } from '../models/search-response.model';
import { CrudOperationEnum } from '../../models/enums/crud-operation.enum';
import { DbContext } from '../utils/db-context';
import { AllocationModel } from '../../models/allocation.model';
import { IDBPTransaction } from 'idb';

@Injectable()
export abstract class BaseEntityRepository<
    T extends BaseEntityModel
> extends BaseRepository<T> {
    async add(data: T): Promise<IDBValidKey> {
        if (!data.id) {
            data.id = Tools.Utils.generateGuid();
        }

        const resp = await this.database.add(this.config.tableName, data);
        await this.markAsChange(CrudOperationEnum.Create, data);

        return resp;
    }

    async edit(data: T): Promise<IDBValidKey> {
        const resp = await this.database.put(this.config.tableName, data);
        await this.markAsChange(CrudOperationEnum.Update, data);
        return resp;
    }

    async delete(id: string): Promise<undefined> {
        const resp = await this.database.delete(this.config.tableName, id);
        await this.markAsChange(CrudOperationEnum.Delete, undefined, id);

        return resp;
    }

    async addRange(data: T[]): Promise<undefined> {
        const transaction = this.database.transaction(
            this.config.tableName,
            'readwrite'
        );

        data.forEach(d => transaction.store.add(d));
        const resp = await transaction.done;
        await this.markAsChangeRange(CrudOperationEnum.Create, data);

        return resp;
    }

    async editRange(data: T[]): Promise<undefined> {
        const transaction = this.database.transaction(
            this.config.tableName,
            'readwrite'
        );

        if (this.config.tableName === DbContext.Allocations) {
            await Promise.all(
                data.map(data => this._buildAllocation(data, transaction))
            );
        } else {
            await Promise.all(data.map(d => transaction.store.put(d)));
        }

        const resp = await transaction.done;
        await this.markAsChangeRange(CrudOperationEnum.Update, data);

        return resp;
    }

    async deleteRange(ids: string[]): Promise<undefined> {
        const transaction = this.database.transaction(
            this.config.tableName,
            'readwrite'
        );

        ids.forEach(d => transaction.store.delete(d));
        const resp = await transaction.done;
        await this.markAsChangeRange(CrudOperationEnum.Delete, undefined, ids);

        return resp;
    }

    async deleteAll(): Promise<undefined> {
        return this.database.clear(this.config.tableName);
    }

    async sync(data: SearchResponseModel<T>): Promise<void> {
        if (data.totalRecords > 0) {
            this.skipLocalUpdate = true;
            await this.editRange(data.records);
            this.skipLocalUpdate = false;
        }
    }

    private async _buildAllocation(
        data: T,
        transaction: IDBPTransaction<unknown, [string]>
    ) {
        const customerId = data['key'] as string;
        const providers: ProviderModel[] = [];
        Object.keys(data['value']).forEach(providerId => {
            providers.push(
                new ProviderModel({
                    id: providerId,
                    startDate: data['value'][providerId].startDate,
                    exitDate: data['value'][providerId].exitDate
                })
            );
        });

        return transaction.store.put(
            new AllocationModel({
                id: customerId.trim(),
                customerId: customerId.trim(),
                providers: providers
            })
        );
    }
}
